import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Building2, LogOut, User, Layers } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTenantName } from '../../hooks/useTenantName';
import { displayNameOrShort } from '../../lib/displayName';
import { useAuthStore } from '../../stores/authStore';
import { useBranding } from '../../hooks/useBranding';
import { authApi } from '../../api/endpoints/auth';
import { Button } from '../ui/button';
import { Gate } from '../permissions/Gate';
import { NavGroup } from './NavGroup';
import { SessionContext } from './SessionContext';
import { visiblePlatformGroups } from './platformNavGroups';
import { visibleNavGroups } from './navGroups';
import { TENANT_NAV_GROUPS } from './scopedNavGroups';

// Título del header = nombre del menú de la ruta actual (i18n). Longest-prefix match.
// TODO(cambios grandes del front): mover a `staticData.title` por ruta cuando se refactore el router.
function pageTitleKey(pathname: string): string {
    if (pathname.startsWith('/me/profile')) return 'common:nav.profile';
    if (pathname.startsWith('/me/security')) return 'common:nav.security';
    if (pathname.startsWith('/platform/applications')) return 'common:nav.platform.applications';
    if (pathname.startsWith('/platform/tenants')) return 'common:nav.platform.tenants';
    if (pathname.startsWith('/platform/admins')) return 'common:nav.platform.admins';
    if (pathname.startsWith('/platform/roles')) return 'common:nav.platform.roles';
    if (pathname.startsWith('/platform/branding')) return 'common:nav.platform.branding';
    if (pathname.startsWith('/platform/security')) return 'common:nav.platform.security';
    if (pathname.startsWith('/platform/audit')) return 'common:nav.platform.audit';
    if (pathname.startsWith('/tenant/groups')) return 'common:nav.tenant.groups';
    if (pathname.startsWith('/tenant/users')) return 'common:nav.tenant.users';
    if (pathname.startsWith('/tenant/roles')) return 'common:nav.tenant.roles';
    if (pathname.startsWith('/tenant/branding')) return 'common:nav.tenant.branding';
    if (pathname.startsWith('/tenant/security')) return 'common:nav.tenant.security';
    if (pathname.startsWith('/tenant/audit')) return 'common:nav.tenant.audit';
    if (pathname === '/') return 'common:nav.home';
    return '';
}

type NavTo =
    | '/me/profile'
    | '/platform/tenants'
    | '/platform/applications'
    | '/platform/admins'
    | '/platform/roles'
    | '/platform/branding'
    | '/platform/security'
    | '/platform/audit'
    | '/tenant/groups'
    | '/tenant/users'
    | '/tenant/roles'
    | '/tenant/branding'
    | '/tenant/security'
    | '/tenant/audit';

// Estilo del item activo (fondo + negrita). Compartido por el caso EXACTO (span) y el
// DESCENDIENTE (Link), para que ambos se vean igual de "activos".
const NAV_ITEM_ACTIVE_CLASS =
    'block rounded-lg px-3 py-2 bg-card font-semibold text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06)]';

/**
 * Item del menú lateral con estado activo. Tres casos:
 * - EXACTO (`pathname === to`): se renderiza como `<span>` (NO Link) → clickearlo no re-navega a la
 *   misma ruta (eso disparaba un re-render que rompía el navegador). Queda marcado (fondo + negrita
 *   + `aria-current`).
 * - DESCENDIENTE (estás en una sub-ruta de la sección, `pathname.startsWith(`${to}/`)`): se renderiza
 *   como `<Link>` CLICKEABLE que vuelve al índice de la sección, conservando el estilo activo
 *   (fondo + negrita + `aria-current`). Re-clickear el item activo te lleva de la sub-ruta al índice.
 * - INACTIVO: `<Link>` normal.
 */
function NavItem({ to, pathname, children }: { to: NavTo; pathname: string; children: ReactNode }) {
    const isExact = pathname === to;
    const isDescendant = pathname.startsWith(`${to}/`);
    if (isExact) {
        return (
            <span aria-current="page" className={`${NAV_ITEM_ACTIVE_CLASS} cursor-default`}>
                {children}
            </span>
        );
    }
    if (isDescendant) {
        return (
            <Link
                to={to}
                aria-current="page"
                className={`${NAV_ITEM_ACTIVE_CLASS} transition-colors hover:bg-foreground/[0.08]`}
            >
                {children}
            </Link>
        );
    }
    return (
        <Link
            to={to}
            className="block rounded-lg px-3 py-2 font-medium text-foreground/80 transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
            {children}
        </Link>
    );
}

/** Iniciales (hasta 2 letras) de un nombre legible, para el fallback del logo. */
function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const second = parts[1]?.[0] ?? '';
    return (first + second).toUpperCase() || '?';
}

export function AppShell({ children }: { children: ReactNode }) {
    const { user, hasPermission, hasScopeAccess, isPlatform } = useAuth();
    const tenantName = useTenantName();
    const branding = useBranding();
    const { t } = useTranslation(['common']);
    const router = useRouter();
    const clear = useAuthStore((s) => s.clear);
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const titleKey = pageTitleKey(pathname);
    const pageTitle = titleKey ? t(titleKey) : '';
    // logoError derivado: true solo si la URL que falló es la que se está mostrando.
    // Cuando branding.logoUrl cambia, vuelve a false solo (sin effect ni setState en effect).
    const [erroredLogoUrl, setErroredLogoUrl] = useState<string | null>(null);
    const logoError = erroredLogoUrl === branding.logoUrl;

    const [loggingOut, setLoggingOut] = useState(false);

    const handleLogout = async (): Promise<void> => {
        if (loggingOut) return;
        setLoggingOut(true);
        // Hay que ESPERAR a que /auth/logout invalide la cookie antes de navegar: si limpiamos
        // y vamos a /login con la cookie todavía viva, el beforeLoad de /login re-hidrata el
        // perfil y rebota al usuario adentro (bug "necesita 2 clicks"). Techo de 4s para no
        // colgar la UI si la lambda está fría. globalSignOut via cognitoSub/tenantId.
        const ids = user ? { cognitoSub: user.cognitoSub, tenantId: user.tenantId } : undefined;
        await Promise.race([
            authApi.logout(ids).catch(() => undefined),
            new Promise<void>((resolve) => {
                setTimeout(resolve, 4000);
            }),
        ]);
        clear();
        void router.navigate({ to: '/login' });
    };

    if (!user) return null;
    const tid = user.tenantId;
    // DEC-013: nombre legible del tenant para el encabezado de la sección TENANT — NUNCA el tenantId
    // crudo. `tenantName` viene de useTenantName() (assignment TENANT) y `displayNameOrShort` corta el
    // caso en que el backend lo pobló con el id crudo.
    const tenantSectionLabel = displayNameOrShort(tenantName, tid);
    // Grupos scoped visibles. Cada canSee replica el <Gate> de su sección (permiso + scope). El
    // usuario Platform usa la vista superior multi-tenant: NO ve la sección TENANT (cableada a su
    // user.tenantId), por eso va con `&& !isPlatform`.
    const tenantGroups = visibleNavGroups(TENANT_NAV_GROUPS, (p) =>
        hasPermission(p) ? hasScopeAccess('TENANT', tid) : false,
    );
    // Mostrar la sección solo si AL MENOS UN item es visible (== había permiso+scope para algún item).
    const showTenantSection = !isPlatform && tenantGroups.length > 0;

    // Menú PLATFORM agrupado en acordeón. Los grupos visibles se calculan con el MISMO chequeo que
    // los <Gate> internos (hasPermission + scope PLATFORM), así un grupo sin items visibles no
    // renderiza header huérfano. El acordeón es exclusivo y auto-abre el grupo de la ruta activa.
    const platformGroups = visiblePlatformGroups((permission) =>
        hasPermission(permission) ? hasScopeAccess('PLATFORM') : false,
    );

    return (
        <div className="flex h-screen overflow-hidden bg-background text-foreground">
            <aside className="w-64 shrink-0 flex flex-col bg-background text-foreground border-r border-border">
                <div className="p-4 border-b border-border text-center shrink-0">
                    {logoError ? (
                        // Fallback del logo: iniciales del NOMBRE del tenant (no del UUID).
                        <span className="font-bold text-xl text-current">
                            {initialsOf(tenantName ?? tid)}
                        </span>
                    ) : (
                        <img
                            src={branding.logoUrl}
                            alt="Logo"
                            className="block mx-auto h-auto"
                            style={{ width: `${String(branding.logoWidth ?? 100)}%` }}
                            onError={() => {
                                setErroredLogoUrl(branding.logoUrl);
                            }}
                        />
                    )}
                </div>
                <SessionContext pathname={pathname} />
                <nav className="flex-1 overflow-y-auto p-2 space-y-1 text-sm">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 mb-1">
                        <User className="size-3 shrink-0 opacity-60" />
                        <span className="text-xs font-semibold uppercase tracking-wider opacity-60">
                            {t('common:nav.account.section')}
                        </span>
                    </div>
                    <NavItem to="/me/profile" pathname={pathname}>
                        {t('common:nav.profile')}
                    </NavItem>

                    {isPlatform && platformGroups.length > 0 && (
                        <div className="pt-3 mt-1 border-t border-border">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 mb-1">
                                <Layers className="size-3 shrink-0 opacity-60" />
                                <span className="text-xs font-semibold uppercase tracking-wider opacity-60">
                                    {t('common:nav.platform.section')}
                                </span>
                            </div>
                            {/* Grupos SIEMPRE desplegados (sin acordeón). visiblePlatformGroups ya
                                filtró items/grupos sin permiso; los <Gate> internos quedan como
                                defensa en profundidad (mismo permiso/scope que el filtro). */}
                            {platformGroups.map((group) => (
                                <NavGroup key={group.id} title={t(group.titleKey)} alwaysOpen>
                                    {group.items.map((item) => (
                                        <Gate
                                            key={item.to}
                                            permission={item.permission}
                                            scope="PLATFORM"
                                        >
                                            <NavItem to={item.to} pathname={pathname}>
                                                {t(item.labelKey)}
                                            </NavItem>
                                        </Gate>
                                    ))}
                                </NavGroup>
                            ))}
                        </div>
                    )}

                    {showTenantSection && (
                        <div className="pt-3 mt-1 border-t border-border">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 mb-1">
                                <Building2 className="size-3 shrink-0 opacity-60" />
                                <span
                                    className="text-xs font-semibold uppercase tracking-wider opacity-60 truncate"
                                    title={tenantSectionLabel}
                                >
                                    {t('common:nav.tenant.section')}
                                </span>
                                <span
                                    className="ml-auto text-xs opacity-50 truncate max-w-[80px]"
                                    title={tenantSectionLabel}
                                >
                                    {tenantSectionLabel}
                                </span>
                            </div>
                            {tenantGroups.map((group) => (
                                <NavGroup key={group.id} title={t(group.titleKey)} alwaysOpen>
                                    {group.items.map((item) => (
                                        <Gate
                                            key={item.to}
                                            permission={item.permission}
                                            scope="TENANT"
                                            scopeRef={tid}
                                        >
                                            <NavItem to={item.to} pathname={pathname}>
                                                {t(item.labelKey)}
                                            </NavItem>
                                        </Gate>
                                    ))}
                                </NavGroup>
                            ))}
                        </div>
                    )}
                </nav>
                <div className="p-2 border-t border-border shrink-0">
                    <Button
                        variant="ghost"
                        disabled={loggingOut}
                        className="w-full justify-start text-current hover:bg-foreground/5 hover:text-current"
                        onClick={() => {
                            void handleLogout();
                        }}
                    >
                        <LogOut className="size-4 mr-2" />{' '}
                        {loggingOut ? t('common:loggingOut') : t('common:logout')}
                    </Button>
                </div>
            </aside>
            <main className="flex-1 flex flex-col min-w-0">
                <header className="shrink-0 border-b border-border bg-background/70 backdrop-blur text-foreground">
                    <div className="mx-auto flex w-full max-w-6xl items-center px-6 py-4">
                        <h1 className="text-lg font-semibold tracking-tight">{pageTitle}</h1>
                    </div>
                </header>
                <div className="flex-1 overflow-auto">
                    <div className="mx-auto w-full max-w-6xl px-6 py-6">{children}</div>
                </div>
            </main>
        </div>
    );
}
